import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowRight, Rocket, Briefcase, Building2, Landmark, ShieldCheck,
  Mail, Lock, Eye, EyeOff,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import PublicNavbar from '@/components/PublicNavbar';
import SovereigntyBanner from '@/components/SovereigntyBanner';
import NetworkCanvas from '@/components/NetworkCanvas';
import { AUTH } from '@/constants/testIds';

/* -------------------------------------------------------------------------- */
/*  Role catalog                                                              */
/* -------------------------------------------------------------------------- */
const ROLES = [
  {
    id: 'founder',
    label: 'Founder',
    sub: 'Build & raise',
    icon: Rocket,
    quote: '"From idea to vetted in 14 days. AI-driven due diligence, sovereign-grade trust."',
    note: 'Submit your venture, complete the AI Q&A, and unlock NDA + MOU partnerships with the holding.',
  },
  {
    id: 'investor',
    label: 'Investor',
    sub: 'Browse vetted opportunities',
    icon: Briefcase,
    quote: '"Twenty-six AI advisors, one thousand years of sectoral mastery — each opportunity pre-cleared."',
    note: 'See only psychometrically-vetted founders. Every deck triaged by the AI Council before it reaches you.',
  },
  {
    id: 'holding_admin',
    label: 'Holding · Incubator',
    sub: 'Run the platform',
    icon: Building2,
    quote: '"Govern your portfolio with the same instruments BlackRock and Diligent give their boards."',
    note: 'Full Entity OS, approval workflows, AI drafting, board minutes, and white-label branding for your tenant.',
  },
  {
    id: 'government',
    label: 'Government · Sovereign',
    sub: 'Regulator & public-sector',
    icon: Landmark,
    quote: '"Aligned to Qatar NDS3 2024-2030. Audit-by-default. Cryptographically attested at rest and in transit."',
    note: 'Cross-entity oversight, regulatory dashboards, and audit-grade reporting calibrated for sovereign use.',
  },
  {
    id: 'platform_admin',
    label: 'Platform Admin',
    sub: 'M3 / Vigilant operator',
    icon: ShieldCheck,
    quote: '"You hold the keys. Twenty-six AIs, hundreds of tenants, one platform — yours."',
    note: 'Cross-tenant audit, MRR tracking, AI training cycles, and sub-domain provisioning.',
  },
];

/* -------------------------------------------------------------------------- */
/*  Login page — v266 international-standard email + password + Remember me  */
/* -------------------------------------------------------------------------- */
const ROLE_KEY = 've_login_role';
const REMEMBER_EMAIL_KEY = 've_login_remember_email';

export default function Login() {
  const { effective } = useTheme() || { effective: 'dark' };
  const isDark = effective !== 'light';

  const queryEmail = (typeof window !== 'undefined')
    ? new URLSearchParams(window.location.search).get('email')
    : null;
  const justReset = (typeof window !== 'undefined')
    && new URLSearchParams(window.location.search).get('reset') === '1';
  const savedEmail = (typeof window !== 'undefined')
    ? (localStorage.getItem(REMEMBER_EMAIL_KEY) || '')
    : '';

  const [email, setEmail] = useState(queryEmail || savedEmail || '');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [rememberEmail, setRememberEmail] = useState(Boolean(savedEmail));
  const [busy, setBusy] = useState(false);

  const { login, user, loading: authLoading } = useAuth() || {};
  const [role, setRole] = useState(() => {
    if (typeof window === 'undefined') return 'holding_admin';
    return localStorage.getItem(ROLE_KEY) || 'holding_admin';
  });
  const nav = useNavigate();
  const loc = useLocation();

  // v270.3 — Trusted-device fix: if the user is ALREADY authenticated
  // (valid 30-day session cookie) and lands on /login (via nav bar,
  // bookmark, or reopened browser), auto-redirect to /dashboard so we
  // never re-ask for password. AuthContext already fired /auth/me on
  // mount; we just need to react to its result here.
  useEffect(() => {
    if (!authLoading && user) {
      const target = loc.state?.from?.pathname || '/dashboard';
      nav(target, { replace: true });
    }
  }, [authLoading, user, nav, loc.state]);

  useEffect(() => {
    try { localStorage.setItem(ROLE_KEY, role); } catch { /* ignore */ }
  }, [role]);

  useEffect(() => {
    if (justReset) {
      toast.success('Password updated — sign in with your new password.');
    }
  }, [justReset]);

  const active = ROLES.find((r) => r.id === role) || ROLES[2];
  const ActiveIcon = active.icon;

  const submit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!email.trim() || !password) return;
    setBusy(true);
    try {
      if (rememberEmail) {
        localStorage.setItem(REMEMBER_EMAIL_KEY, email.trim().toLowerCase());
      } else {
        localStorage.removeItem(REMEMBER_EMAIL_KEY);
      }
    } catch { /* ignore quota / private-mode failures */ }
    try {
      await login(email.trim().toLowerCase(), password, rememberMe);
      toast.success(rememberMe
        ? 'Signed in — stay-logged-in for 30 days on this browser.'
        : 'Signed in.');
      const target = loc.state?.from?.pathname || '/dashboard';
      nav(target, { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || 'Sign-in failed');
    } finally { setBusy(false); }
  };

  return (
    <main
      data-testid="login-page"
      className={`relative min-h-screen overflow-hidden ${isDark ? 'text-white' : 'text-slate-900'}`}
      style={{
        background: isDark
          ? 'radial-gradient(1300px 700px at 85% -10%, rgba(174,82,234,0.18), transparent 60%),'
            + ' radial-gradient(1000px 700px at -10% 110%, rgba(94,31,139,0.16), transparent 60%),'
            + ' radial-gradient(900px 900px at 50% 50%, rgba(121,50,174,0.08), transparent 70%),'
            + ' #000000'
          // v266.5/6 — soft lavender wash instead of stark white. The
          // base color is now sourced from `var(--ve-bg)` so all public
          // surfaces share one violet-tinted paper. Boosted stops so
          // the background actually reads.
          : 'radial-gradient(1300px 700px at 85% -10%, rgba(174,82,234,0.18), transparent 60%),'
            + ' radial-gradient(1000px 700px at -10% 110%, rgba(94,31,139,0.15), transparent 60%),'
            + ' radial-gradient(900px 900px at 50% 50%, rgba(121,50,174,0.10), transparent 70%),'
            + ' var(--ve-bg)',
      }}
    >
      <NetworkCanvas isDark={isDark} testId="login-node-canvas" />
      <PublicNavbar variant={isDark ? 'dark' : 'light'} />

      <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-12 pt-12 lg:pt-20 pb-16 grid gap-12 lg:grid-cols-[1.1fr_1fr] items-center">
        {/* Left — narrative */}
        <section className="space-y-8">
          <div className={`ve-eyebrow ${isDark ? 'text-[#8B3FC5]/80' : 'text-[#5E1F8B]/85'}`}>Sovereign Edition · v266</div>
          <h1 className="display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-[-0.02em]">
            The connected operating system for governance, audit, and venture.
          </h1>

          {/* Role tabs */}
          <div className="space-y-3" data-testid="login-role-tabs">
            <div className={`text-[10px] uppercase tracking-[0.28em] ${isDark ? 'text-white/45' : 'text-slate-500'}`}>Signing in as</div>
            <div className="flex flex-wrap gap-2">
              {ROLES.map((r) => {
                const Icon = r.icon;
                const isActive = r.id === role;
                return (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => setRole(r.id)}
                    data-testid={`login-role-${r.id}`}
                    className={`group inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-xs transition-all
                      ${isActive
                        ? 'bg-[#8B3FC5] text-white shadow-[0_0_28px_-6px_rgba(174,82,234,0.65)] border border-[#8B3FC5]'
                        : isDark
                          ? 'border border-white/15 text-white/65 hover:text-white hover:border-[#8B3FC5]/60 hover:bg-white/[0.03]'
                          : 'border border-slate-300 text-slate-600 hover:text-slate-900 hover:border-[#8B3FC5]/60 hover:bg-slate-100/60'}
                    `}
                  >
                    <Icon size={13} />
                    <span className="font-medium">{r.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Persona quote card */}
          <div
            key={role}
            data-testid="login-role-quote"
            className={`relative overflow-hidden rounded-xl border border-[#8B3FC5]/25 p-6 lg:p-7 backdrop-blur-sm animate-[fadeUp_0.45s_ease-out] ${
              isDark ? 'bg-white/[0.025]' : 'bg-white/70 shadow-[0_8px_36px_-12px_rgba(94,31,139,0.18)]'
            }`}
          >
            <div className="absolute -top-12 -right-12 w-44 h-44 rounded-full bg-[#8B3FC5]/12 blur-3xl" />
            <div className="flex items-center gap-3">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-[#8B3FC5]/15 text-[#8B3FC5] border border-[#8B3FC5]/30">
                <ActiveIcon size={16} />
              </span>
              <div>
                <div className="text-sm font-semibold tracking-wide">{active.label}</div>
                <div className={`text-[11px] uppercase tracking-[0.22em] ${isDark ? 'text-white/45' : 'text-slate-500'}`}>{active.sub}</div>
              </div>
            </div>
            <p className={`mt-4 text-lg leading-relaxed italic ${isDark ? 'text-white/85' : 'text-slate-700'}`} style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>
              {active.quote}
            </p>
            <p className={`mt-3 text-xs ${isDark ? 'text-white/55' : 'text-slate-500'}`}>{active.note}</p>
          </div>

          <div className={`flex flex-wrap items-center gap-x-5 gap-y-2 text-[11px] uppercase tracking-[0.22em] ${isDark ? 'text-white/40' : 'text-slate-500'}`}>
            <span>AES-256-GCM</span>
            <span className={isDark ? 'text-white/20' : 'text-slate-300'}>·</span>
            <span>TLS 1.3</span>
            <span className={isDark ? 'text-white/20' : 'text-slate-300'}>·</span>
            <span>Audit-by-default</span>
            <span className={isDark ? 'text-white/20' : 'text-slate-300'}>·</span>
            <span>NDS3 2024-2030</span>
          </div>
        </section>

        {/* Right — sign-in form */}
        <section className="relative">
          <div className={`relative rounded-2xl backdrop-blur-xl p-7 lg:p-9 ${
            isDark
              ? 'border border-white/10 bg-white/[0.035] shadow-[0_30px_120px_-40px_rgba(174,82,234,0.4)]'
              : 'border border-slate-200 bg-white/85 shadow-[0_30px_80px_-30px_rgba(94,31,139,0.18)]'
          }`}>
            <div className={`ve-eyebrow ${isDark ? 'text-white/60' : 'text-slate-500'}`}>Sign in</div>
            <h2 className="display text-3xl mt-2">Welcome back.</h2>
            <p className={`text-xs mt-1.5 ${isDark ? 'text-white/55' : 'text-slate-600'}`}>
              Enter your email and password to continue.
            </p>

            <SovereigntyBanner size="sm" className="mt-5 w-full" />

            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="login-form">
              <label className={`block text-[10px] uppercase tracking-[0.22em] ${isDark ? 'text-white/45' : 'text-slate-500'}`}>
                Email
                <div className="relative mt-1.5">
                  <Mail size={13} className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-white/40' : 'text-slate-400'}`} />
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid={AUTH.loginEmail}
                    autoComplete="email"
                    placeholder="you@company.com"
                    className={`w-full rounded-md pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#8B3FC5]/30 focus:border-[#8B3FC5] normal-case tracking-normal ${
                      isDark
                        ? 'border border-white/15 bg-black/40 text-white placeholder-white/30'
                        : 'border border-slate-300 bg-white text-slate-900 placeholder-slate-400'
                    }`}
                  />
                </div>
              </label>

              <label className={`block text-[10px] uppercase tracking-[0.22em] ${isDark ? 'text-white/45' : 'text-slate-500'}`}>
                Password
                <div className="relative mt-1.5">
                  <Lock size={13} className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-white/40' : 'text-slate-400'}`} />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid={AUTH.loginPassword}
                    autoComplete="current-password"
                    placeholder="Your password"
                    className={`w-full rounded-md pl-9 pr-10 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#8B3FC5]/30 focus:border-[#8B3FC5] normal-case tracking-normal ${
                      isDark
                        ? 'border border-white/15 bg-black/40 text-white placeholder-white/30'
                        : 'border border-slate-300 bg-white text-slate-900 placeholder-slate-400'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    data-testid="login-password-toggle"
                    className={`absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded ${
                      isDark ? 'text-white/45 hover:text-white' : 'text-slate-500 hover:text-slate-900'
                    }`}
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
              </label>

              {/* Remember me for 30 days */}
              <label
                className={`flex items-start gap-2 cursor-pointer select-none rounded-md p-3 transition-all ${
                  isDark
                    ? 'border border-white/10 bg-white/[0.025] hover:bg-white/[0.04]'
                    : 'border border-slate-200 bg-slate-50/60 hover:bg-slate-50'
                }`}
                data-testid="login-remember-me-label"
              >
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  data-testid="login-remember-me-checkbox"
                  className="mt-0.5 h-4 w-4 rounded border-slate-400 text-[#8B3FC5] focus:ring-[#8B3FC5]/30 cursor-pointer"
                />
                <div className="text-[12px] leading-snug normal-case tracking-normal">
                  <div className={`font-semibold ${isDark ? 'text-white/90' : 'text-slate-800'}`}>
                    Remember me for 30 days
                  </div>
                  <div className={isDark ? 'text-white/55 mt-0.5' : 'text-slate-500 mt-0.5'}>
                    Stay signed in on this browser. You can revoke devices any time from Profile &rarr; Security.
                  </div>
                </div>
              </label>

              {/* Remember email helper (separate from remember-me session) */}
              <label
                className={`flex items-center gap-2 cursor-pointer select-none text-[11px] normal-case tracking-normal ${
                  isDark ? 'text-white/70' : 'text-slate-600'
                }`}
                data-testid="login-remember-email-label"
              >
                <input
                  type="checkbox"
                  checked={rememberEmail}
                  onChange={(e) => setRememberEmail(e.target.checked)}
                  data-testid="login-remember-email-checkbox"
                  className="h-3.5 w-3.5 rounded border-slate-400 text-[#8B3FC5] focus:ring-[#8B3FC5]/30 cursor-pointer"
                />
                Remember my email on this device
              </label>

              <button
                type="submit"
                disabled={busy || !email.trim() || !password}
                data-testid={AUTH.loginSubmit}
                className="group relative w-full inline-flex items-center justify-center gap-2 rounded-md bg-[#8B3FC5] text-white text-sm font-semibold px-4 py-2.5 transition-all hover:bg-[#AE52EA] hover:shadow-[0_0_36px_-4px_rgba(174,82,234,0.7)] disabled:opacity-60"
              >
                {busy ? 'Signing in…' : 'Sign in'}
                <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
              </button>

              <div className={`flex items-center justify-between text-[11px] ${isDark ? 'text-white/55' : 'text-slate-500'}`}>
                <Link
                  to="/forgot-password"
                  data-testid="login-forgot-password"
                  className={`tracking-wide transition-colors ${
                    isDark ? 'text-[#AE52EA] hover:text-white' : 'text-[#8B3FC5] hover:text-[#5E1F8B]'
                  }`}
                >
                  Forgot your password?
                </Link>
                <Link
                  to="/signup"
                  data-testid="login-signup-link"
                  className={`tracking-wide transition-colors ${
                    isDark ? 'text-white/70 hover:text-white' : 'text-slate-700 hover:text-slate-900'
                  }`}
                >
                  Create an account &rarr;
                </Link>
              </div>
            </form>

            <p className={`mt-7 text-xs leading-relaxed ${isDark ? 'text-white/55' : 'text-slate-600'}`}>
              By signing in you agree to the{' '}
              <Link to="/terms" className={isDark ? 'underline decoration-white/30 hover:decoration-white' : 'underline decoration-slate-400 hover:decoration-slate-900'}>Terms</Link>
              {' '}and{' '}
              <Link to="/privacy" className={isDark ? 'underline decoration-white/30 hover:decoration-white' : 'underline decoration-slate-400 hover:decoration-slate-900'}>Privacy Policy</Link>.
            </p>
          </div>
        </section>
      </div>

      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </main>
  );
}
